import React from 'react';
import {
  TrendingUp, Gavel, Wrench, Receipt, KeyRound, Users, AlertTriangle,
} from 'lucide-react';

const ISSUES = [
  {
    icon: TrendingUp,
    title: 'Unprotected Rent Reviews',
    desc: 'Upward-only rent review clauses without cap mechanisms can expose tenants to unchecked market rate increases.',
    consequence: 'Rent obligations escalate beyond budget with no downward correction possible',
  },
  {
    icon: Gavel,
    title: 'LTA 1954 Contracting Out Failures',
    desc: 'Failure to correctly follow the statutory contracting-out procedure means the tenant retains security of tenure.',
    consequence: 'Landlord loses the ability to recover possession at lease expiry',
  },
  {
    icon: Wrench,
    title: 'Vague Dilapidations Standards',
    desc: 'Ambiguous repair and reinstatement clauses create uncertainty over end-of-lease obligations for both parties.',
    consequence: 'Disputes costing \u00A310,000 - \u00A350,000+ at lease end with no clear standard',
  },
  {
    icon: Receipt,
    title: 'Service Charge Ambiguity',
    desc: 'Uncapped service charges and unclear management fee provisions allow costs to spiral without tenant recourse.',
    consequence: 'Annual service charge bills increase 30-50% with no mechanism to challenge',
  },
  {
    icon: KeyRound,
    title: 'Break Clause Conditions Not Met',
    desc: 'Break clauses with unachievable conditions precedent become effectively unexercisable.',
    consequence: 'Tenant trapped in lease for full term despite having a nominal break right',
  },
  {
    icon: Users,
    title: 'Assignment & Alienation Restrictions',
    desc: 'Overly restrictive alienation provisions prevent the tenant from assigning, subletting, or adapting to business changes.',
    consequence: 'Business locked into premises with no exit route even if circumstances change',
  },
];

export function CommercialComplianceIssues() {
  return (
    <section className="py-20 sm:py-28 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-6">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Critical Commercial Lease Risks</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Avoid These Costly Compliance Mistakes
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Issues discovered in 75% of commercial leases we review
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {ISSUES.map((issue, i) => {
            const Icon = issue.icon;
            return (
              <div
                key={issue.title}
                className={`animate-on-scroll delay-${(i % 3) * 100 + 100} bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-elevated transition-shadow duration-300`}
              >
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{issue.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{issue.desc}</p>
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-red-800 uppercase tracking-wider mb-1">Consequence</p>
                  <p className="text-sm text-red-700 leading-relaxed">{issue.consequence}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="animate-on-scroll bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 sm:p-10 text-center">
          <h3 className="text-xl font-bold text-white mb-3">The Real Cost of Getting It Wrong</h3>
          <p className="text-gray-300 text-sm max-w-2xl mx-auto leading-relaxed mb-6">
            A single overlooked clause in a commercial lease can expose you to tens of thousands in unexpected costs,
            lost flexibility, and unenforceable provisions.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-6 py-3">
            <span className="text-white/70 text-sm">Average legal cost to fix compliance issues after the fact:</span>
            <span className="text-white font-bold text-lg">&pound;2,000 - &pound;5,000+</span>
          </div>
        </div>
      </div>
    </section>
  );
}
